import React, { useState } from "react";

import { useBankList, usePunchPayment } from "store/backend/assets";
import {
  INDIAN_CURRENCY_ID,
  MAX_BANK_TRANSFER_REFERENCE_ID_LENGTH,
  PAYMENT_MODE,
} from "constants/config";
import {
  DatePicker,
  PrimaryButton,
  SingleSelect,
  Text,
  Textfield,
} from "widgets";
import Loader from "Shared/Loader/Loader";
import dayjs from "dayjs";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import styles from "./OfflinePayments.module.scss";

const buildPayloadForCashPayment = (orderId, amount, createdBy) => ({
  order_id: orderId,
  amount: amount,
  currency_id: INDIAN_CURRENCY_ID,
  cash: {
    created_by: createdBy,
  },
});

const buildPayloadForChequePayment = (
  orderId,
  amount,
  chequeDetails,
  createdBy
) => ({
  order_id: orderId,
  amount: amount,
  currency_id: INDIAN_CURRENCY_ID,
  cheque: {
    cheque_number: chequeDetails.chequeNumber,
    bank_id: chequeDetails.bankId,
    created_by: createdBy,
    pdc_date: chequeDetails.pdcDate,
  },
});

const buildPayloadForBankTransferPayment = (
  orderId,
  amount,
  bankTransferDetails,
  createdBy,
  referenceId
) => ({
  order_id: orderId,
  amount: amount,
  currency_id: INDIAN_CURRENCY_ID,
  bank_transfer: {
    reference_transaction_id: referenceId,
    bank_id: bankTransferDetails.bankId,
    created_by: createdBy,
  },
});
const handleOnClickPunchPaymentButton = (
  orderId,
  payableAmount,
  createdBy,
  paymentMode,
  chequeDetails,
  bankTransferDetails,
  referenceId,
  punchPayment,
  openSnackbar
) => {
  const payload =
    paymentMode === PAYMENT_MODE.CASH
      ? buildPayloadForCashPayment(orderId, payableAmount, createdBy)
      : paymentMode === PAYMENT_MODE.CHEQUE
      ? buildPayloadForChequePayment(
          orderId,
          payableAmount,
          chequeDetails,
          createdBy
        )
      : paymentMode === PAYMENT_MODE.BANK_TRANSFER
      ? buildPayloadForBankTransferPayment(
          orderId,
          payableAmount,
          bankTransferDetails,
          createdBy,
          referenceId
        )
      : null;

  if (!payload) {
    openSnackbar("Invalid payment mode selected", "error");
    return;
  }

  punchPayment(payload, {
    onSuccess: () => {
      openSnackbar("Payment successful", "success");
      setTimeout(() => window.location.reload(), 1000);
    },
    onError: (error) => {
      openSnackbar(error.message, "error");
    },
  });
};
const shouldEnablePunchPaymentButton = (
  paymentMode,
  chequeNumber,
  bankId,
  pdcDate,
  bankReferenceId
) => {
  if (!paymentMode) {
    return true;
  }

  if (paymentMode === PAYMENT_MODE.CASH) {
    return false;
  }

  if (paymentMode === PAYMENT_MODE.CHEQUE) {
    return !chequeNumber || !bankId || !pdcDate;
  }
  if (paymentMode === PAYMENT_MODE.BANK_TRANSFER) {
    return !bankReferenceId;
  }

  return true;
};

const CashAndChequePayment = (props) => {
  const { orderId, payableAmount, createdBy, paymentMode } = props;
  const { openSnackbar } = useSnackbarContext();
  const [chequeNumber, setChequeNumber] = useState("");
  const [bankId, setBankId] = useState(null);
  const [pdcDate, setPdcDate] = useState("");
  const [bankReferenceId, setBankReferenceId] = useState("");
  const { data: bankList, isPending: isBankListLoading } = useBankList();
  const { mutate: punchPayment, isPending: isPunchPaymentLoading } =
    usePunchPayment();
  const bankMenuItems = bankList?.map(({ id, name }) => ({
    key: id,
    value: id,
    label: name,
  }));
  const minDate = dayjs().subtract(21, "day");
  const maxDate = dayjs().add(1, "year");

  return (
    <div className={styles.mainContainer}>
      {paymentMode === PAYMENT_MODE.CHEQUE && (
        <div className={styles.chequePaymentContainer}>
          <div className={styles.field}>
            <Text>Please enter cheque number</Text>
            <Textfield
              value={chequeNumber}
              handleOnChange={(e) => {
                const onlyNumbers = e.target.value.replace(/\D/g, "");
                setChequeNumber(onlyNumbers);
              }}
              placeholder="Enter Cheque Number"
            />
          </div>
          <div className={styles.flexContainer}>
            {isBankListLoading ? (
              <Loader />
            ) : (
              <div className={styles.field}>
                <Text>Please select bank</Text>
                <SingleSelect
                  value={bankId}
                  menuItems={bankMenuItems}
                  handleChange={(e) => setBankId(e.target.value)}
                  placeholder="Select Bank"
                  tag="Bank"
                />
              </div>
            )}
            <div className={styles.field}>
              <Text>Please select cheque date</Text>
              <DatePicker
                value={pdcDate}
                onChange={(date) => setPdcDate(date)}
                placeholder="Select Cheque Date"
                minDate={minDate}
                maxDate={maxDate}
              />
            </div>
          </div>
        </div>
      )}
      {paymentMode === PAYMENT_MODE.BANK_TRANSFER && (
        <div className={styles.bankTransferContainer}>
          <div className={styles.field}>
            <Text>Please enter reference ID</Text>
            <Textfield
              value={bankReferenceId}
              handleOnChange={(e) => {
                const validInput = e.target.value
                  .replace(/\s+/g, "")
                  .slice(0, MAX_BANK_TRANSFER_REFERENCE_ID_LENGTH);
                setBankReferenceId(validInput);
              }}
              placeholder="Enter Reference ID"
            />
          </div>
          <div className={styles.flexContainer}>
            {isBankListLoading ? (
              <Loader />
            ) : (
              <div className={styles.field}>
                <Text>Please select bank</Text>
                <SingleSelect
                  value={bankId}
                  menuItems={bankMenuItems}
                  handleChange={(e) => setBankId(e.target.value)}
                  placeholder="Select Bank"
                  tag="Bank"
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className={styles.submitButton}>
        <PrimaryButton
          onClick={() =>
            handleOnClickPunchPaymentButton(
              orderId,
              payableAmount,
              createdBy,
              paymentMode,
              { chequeNumber, bankId, pdcDate },
              { bankId },
              bankReferenceId,
              punchPayment,
              openSnackbar
            )
          }
          disabled={shouldEnablePunchPaymentButton(
            paymentMode,
            chequeNumber,
            bankId,
            pdcDate,
            bankReferenceId
          )}
        >
          {isPunchPaymentLoading ? <Loader /> : "Create Payment"}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default CashAndChequePayment;
